import React, { useState } from "react";
import { IClinicInfo, IOpeningHour } from "../types/clinic.types";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import bookUrl from "../../../../api/bookUrl";
import Select from "react-select";
import jsonData from "../../../utils/location/cities-and-postalcode-by-district.json";
// --- TYPE DEFINITIONS ---

// Shape of a single city in your JSON
type City = {
  city: string;
  code: string;
};

// Shape of the entire JSON data
type CityDataType = Record<string, City[]>;

// A. Option type for the FIRST select (Districts)
type DistrictOption = {
  value: string; // "Ratnapura", "Colombo", etc.
  label: string;
};

// B. Option type for the SECOND select (Cities)
type CityOption = {
  value: string; // "70082", "10150", etc. (the code)
  label: string; // "Akarella", "Athurugiriya", etc. (the city name)
};

// --- DATA PREPARATION ---

// Cast the imported JSON to our defined type
const cityData: CityDataType = jsonData;

// Get the district names from the keys
const districtKeys = Object.keys(cityData);

// Format the district keys for the first react-select
const districtOptions: DistrictOption[] = districtKeys.map((key) => ({
  value: key,
  label: key,
}));

interface Props {
  info: IClinicInfo;
  onInfoChange: (info: IClinicInfo) => void;
}

export const ClinicInfo: React.FC<Props> = ({ info, onInfoChange }) => {
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] =
    useState<DistrictOption | null>(null);
  const [localInfo, setLocalInfo] = useState<IClinicInfo>(info);
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.name === "name") {
      setLocalInfo({
        ...localInfo,
        [e.target.name]: e.target.value,
        slug: generateSlug(e.target.value),
      });
    } else {
      setLocalInfo({ ...localInfo, [e.target.name]: e.target.value });
    }
  };

  const handleHourChange = (
    index: number,
    field: keyof IOpeningHour,
    value: string | boolean
  ) => {
    const newHours = [...localInfo.openingHours];
    (newHours[index] as any)[field] = value;
    setLocalInfo({ ...localInfo, openingHours: newHours });
  };

  const handleSave = () => {
    onInfoChange(localInfo);
    const newdata = {
      ...localInfo,
      district: selectedDistrict?.value,
      city: selectedCity?.value,
    };

    bookUrl
      .patch("/bookingclinic/clinic/addBookClinicInfo", newdata)
      .then((response) => {
        console.log(response);
      })
      .catch(console.error)
      .finally(() => {});
  };

  const handleDistrictChange = (selectedOption: DistrictOption | null) => {
    setSelectedDistrict(selectedOption);
    setSelectedCity(null);
    setCityOptions([]);
    if (selectedOption) {
      const rawCities = cityData[selectedOption.value] || [];
      const formattedCityOptions = rawCities.map((city: City) => ({
        value: city.code,
        label: city.city,
      }));
      setCityOptions(formattedCityOptions);
    }
  };

  const handleCityChange = (selectedOption: CityOption | null) => {
    setSelectedCity(selectedOption);
  };
  return (
    <section className="page-section clinic-info">
      <div className="section-header">
        <h2>Basic Information</h2>
        <button className="btn btn-primary" onClick={handleSave}>
          <SaveAltIcon /> Save
        </button>
      </div>

      <form>
        <div className="form-group">
          <label htmlFor="name">Clinic Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={localInfo.name}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="name">Slug</label>
          <input
            type="text"
            id="name"
            name="slug"
            value={localInfo.slug}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={localInfo.description}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="district-select">Choose a District:</label>
          <Select
            id="district-select"
            options={districtOptions}
            value={selectedDistrict}
            onChange={handleDistrictChange}
            isSearchable
            isClearable
            placeholder="Type to search districts..."
          />
        </div>
        <div className="form-group">
          <label htmlFor="city-select">Choose a City:</label>
          <Select
            id="city-select"
            options={cityOptions} // Options are now from state
            value={selectedCity} // Value is from state
            onChange={handleCityChange} // Handler is new
            isSearchable
            isClearable
            // Disable until a district is selected
            isDisabled={!selectedDistrict}
            placeholder={
              selectedDistrict
                ? "Type to search cities..."
                : "-- Choose a district first --"
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="location">street</label>
          <input
            type="text"
            id="location"
            name="street"
            value={localInfo.street}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="location">address line</label>
          <input
            type="text"
            id="location"
            name="addressLine"
            value={localInfo.addressLine}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="location">embed map</label>
          <input
            type="text"
            id="location"
            name="map"
            value={localInfo.map}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={localInfo.email}
            onChange={handleChange}
          />
        </div>
      </form>

      <h3 style={{ marginTop: "1.5rem" }}>Opening Hours</h3>
      <div className="opening-hours-list">
        {localInfo.openingHours.map((day, index) => (
          <div
            key={day.day}
            className={`day-schedule ${day.isClosed ? "is-closed" : ""}`}
          >
            <strong className="day-label">{day.day}</strong>

            {!day.isClosed ? (
              <span className="time-inputs">
                <input
                  type="time"
                  value={day.openTime}
                  className={`time-input ${day.isClosed ? "closed" : ""}`}
                  onChange={(e) =>
                    handleHourChange(index, "openTime", e.target.value)
                  }
                />
                <input
                  type="time"
                  value={day.closeTime}
                  className={`time-input ${day.isClosed ? "closed" : ""}`}
                  onChange={(e) =>
                    handleHourChange(index, "closeTime", e.target.value)
                  }
                />
              </span>
            ) : (
              <span className="closed-text">Closed</span>
            )}

            <div className="form-check">
              <input
                type="checkbox"
                id={`closed-${day.day}`}
                checked={day.isClosed}
                onChange={(e) =>
                  handleHourChange(index, "isClosed", e.target.checked)
                }
              />
              <label htmlFor={`closed-${day.day}`}>Closed</label>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
